import { useState, useEffect } from 'react'
import {
  Box,
  Paper,
  Typography,
  Chip,
  Button,
  CircularProgress,
  Alert,
  Divider,
  Stack,
} from '@mui/material'
import {
  CheckCircle,
  Warning,
  Schedule,
  CreditCard,
  AutorenewOutlined,
} from '@mui/icons-material'
import { subscriptionsApi } from '@api/endpoints/subscriptions'
import type { UserSubscriptionDto } from '@api/types/subscription'
import { format } from 'date-fns'

interface CurrentPlanSectionProps {
  onUpgradeClick?: () => void
}

export function CurrentPlanSection({ onUpgradeClick }: CurrentPlanSectionProps) {
  const [subscription, setSubscription] = useState<UserSubscriptionDto | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    loadSubscription()
  }, [])

  const loadSubscription = async () => {
    try {
      setIsLoading(true)
      setError(null)
      const data = await subscriptionsApi.getCurrentSubscription()
      setSubscription(data)
    } catch (err) {
      setError('Failed to load subscription information')
      console.error('Error loading subscription:', err)
    } finally {
      setIsLoading(false)
    }
  }

  const getStatusColor = (status: string): 'success' | 'warning' | 'error' | 'default' => {
    switch (status) {
      case 'Active':
      case 'Trial':
        return 'success'
      case 'PastDue':
      case 'Paused':
        return 'warning'
      case 'Cancelled':
      case 'Expired':
        return 'error'
      default:
        return 'default'
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'Active':
        return <CheckCircle fontSize="small" />
      case 'Trial':
        return <Schedule fontSize="small" />
      case 'PastDue':
      case 'Paused':
      case 'Cancelled':
      case 'Expired':
        return <Warning fontSize="small" />
      default:
        return null
    }
  }

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>
  }

  if (!subscription) {
    return <Alert severity="info">No subscription information available</Alert>
  }

  return (
    <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
            {subscription.plan.displayName}
          </Typography>
          <Chip
            icon={getStatusIcon(subscription.status)}
            label={subscription.statusDisplayName}
            color={getStatusColor(subscription.status)}
            size="small"
          />
        </Box>
        {subscription.plan.isFreeTier && onUpgradeClick && (
          <Button variant="contained" color="primary" onClick={onUpgradeClick}>
            Upgrade Plan
          </Button>
        )}
      </Box>

      <Divider sx={{ my: 2 }} />

      <Stack spacing={2}>
        {/* Trial information */}
        {subscription.trialDaysRemaining !== null && (
          <Alert severity="info" icon={<Schedule />}>
            <Typography variant="body2">
              <strong>{subscription.trialDaysRemaining} days</strong> remaining in your trial.
              {subscription.trialEndDate && (
                <> Trial ends on {format(new Date(subscription.trialEndDate), 'MMMM d, yyyy')}.</>
              )}
            </Typography>
          </Alert>
        )}

        {/* Cancellation notice */}
        {subscription.cancelledAt && subscription.isActive && (
          <Alert severity="warning">
            <Typography variant="body2">
              Your subscription has been cancelled and will end on{' '}
              {format(new Date(subscription.currentPeriodEnd), 'MMMM d, yyyy')}.
            </Typography>
          </Alert>
        )}

        {/* Billing details */}
        {!subscription.plan.isFreeTier && (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AutorenewOutlined color="action" />
              <Typography variant="body2" color="text.secondary">
                Billing: <strong>{subscription.billingInterval}</strong>
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Schedule color="action" />
              <Typography variant="body2" color="text.secondary">
                Current period:{' '}
                <strong>
                  {format(new Date(subscription.currentPeriodStart), 'MMM d, yyyy')} -{' '}
                  {format(new Date(subscription.currentPeriodEnd), 'MMM d, yyyy')}
                </strong>
              </Typography>
            </Box>

            {subscription.autoRenew && subscription.daysUntilRenewal > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AutorenewOutlined color="action" />
                <Typography variant="body2" color="text.secondary">
                  Renews in <strong>{subscription.daysUntilRenewal} days</strong>
                </Typography>
              </Box>
            )}

            {subscription.paymentMethodDisplay && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CreditCard color="action" />
                <Typography variant="body2" color="text.secondary">
                  Payment method: <strong>{subscription.paymentMethodDisplay}</strong>
                </Typography>
              </Box>
            )}
          </>
        )}

        {subscription.plan.isFreeTier && (
          <Typography variant="body2" color="text.secondary">
            You are currently on the free tier. Upgrade to unlock premium features!
          </Typography>
        )}
      </Stack>
    </Paper>
  )
}
